import React, { createContext, useContext, useState, useEffect } from 'react';
import { authApi, SHOP_SUSPENDED_EVENT, PASSWORD_CHANGE_REQUIRED_EVENT, tokenRequiresPasswordChange } from '../utils/api';
import { hasAdminRights, isOwner as userIsOwner } from '../utils/roles';

const isDevBypass = () => process.env.REACT_APP_DEV_AUTH_BYPASS === 'true';
const DEV_USER = { id: 'dev', name: 'Dev User', email: 'dev@local', role: 'admin' };

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

const AUTH_NOTICE_KEY = 'bouqmark:authNotice';

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [initialized, setInitialized] = useState(false);
  // Why the person was signed out, shown on the sign-in screen (a suspended
  // shop). Kept for this browser tab (sessionStorage), so a reload right after
  // the sign-out still says why (QA B: "bounced with no message").
  const [authNotice, setAuthNoticeState] = useState(() => {
    try { return sessionStorage.getItem(AUTH_NOTICE_KEY) || ''; } catch (_) { return ''; }
  });
  const setAuthNotice = (message) => {
    try {
      if (message) sessionStorage.setItem(AUTH_NOTICE_KEY, message);
      else sessionStorage.removeItem(AUTH_NOTICE_KEY);
    } catch (_) { /* storage unavailable: the notice lasts until a reload */ }
    setAuthNoticeState(message);
  };

  // A suspended shop's open session ends at its next API call (10B): sign out
  // here, without the server logout call (the session is already refused).
  useEffect(() => {
    const onSuspended = (event) => {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      setAuthNotice(event.detail?.message || 'This shop\'s account is suspended.');
      setUser(null);
    };
    window.addEventListener(SHOP_SUSPENDED_EVENT, onSuspended);
    return () => window.removeEventListener(SHOP_SUSPENDED_EVENT, onSuspended);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // A driver on a one-time password is refused everywhere until they pick
  // their own in the Driver app: mark the session so the app shows that
  // screen instead of failing call by call.
  useEffect(() => {
    const onPasswordChange = () => {
      setUser((prev) => {
        if (!prev || prev.mustChangePassword === true) return prev;
        const next = { ...prev, mustChangePassword: true };
        localStorage.setItem('user', JSON.stringify(next));
        return next;
      });
    };
    window.addEventListener(PASSWORD_CHANGE_REQUIRED_EVENT, onPasswordChange);
    return () => window.removeEventListener(PASSWORD_CHANGE_REQUIRED_EVENT, onPasswordChange);
  }, []);

  // Check if user is logged in on app start
  useEffect(() => {
    const initializeAuth = async () => {
      // Local dev bypass — set REACT_APP_DEV_AUTH_BYPASS=true in .env.local
      if (isDevBypass()) {
        console.log('[AuthContext] Dev auth bypass active');
        localStorage.setItem('token', 'dev-bypass-token');
        setUser({ ...DEV_USER, capabilities: ['take_orders', 'view_orders', 'manage_customers', 'drive_delivery'] });
        setLoading(false);
        setInitialized(true);
        return;
      }

      const token = localStorage.getItem('token');
      const storedUser = localStorage.getItem('user');

      if (token && storedUser) {
        try {
          // Verify token is still valid by fetching current user
          // Note: The API layer will automatically refresh the token if needed
          console.log('[AuthContext] Verifying stored token...');
          const response = await authApi.getProfile();
          console.log('[AuthContext] Profile response:', response);

          if (response.success) {
            setUser(response.data.user);
            console.log('[AuthContext] Token verified, user restored:', response.data.user.email || response.data.user.username);
          } else {
            // Token verification failed even after auto-refresh attempt, try manual refresh
            console.warn('[AuthContext] Token verification failed, attempting manual refresh...');
            try {
              await authApi.refreshToken();
              // Retry profile fetch after refresh
              const retryResponse = await authApi.getProfile();
              if (retryResponse.success) {
                setUser(retryResponse.data.user);
                console.log('[AuthContext] Token refreshed and user restored');
              } else {
                throw new Error('Profile fetch failed after refresh');
              }
            } catch (refreshError) {
              console.error('[AuthContext] Token refresh failed:', refreshError.message);
              localStorage.removeItem('token');
              localStorage.removeItem('user');
            }
          }
        } catch (error) {
          // Token is invalid or expired - try manual refresh as a last resort
          console.warn('[AuthContext] Profile fetch failed, attempting manual token refresh...');
          console.error('[AuthContext] Error:', error.message);

          try {
            await authApi.refreshToken();
            // Retry profile fetch after refresh
            const retryResponse = await authApi.getProfile();
            if (retryResponse.success) {
              setUser(retryResponse.data.user);
              console.log('[AuthContext] Token refreshed and user restored after error');
            } else {
              throw new Error('Profile fetch failed after refresh');
            }
          } catch (refreshError) {
            console.error('[AuthContext] Manual token refresh also failed, logging out:', refreshError.message);
            localStorage.removeItem('token');
            localStorage.removeItem('user');
          }
        }
      } else {
        console.log('[AuthContext] No stored token or user found');
      }

      setLoading(false);
      setInitialized(true);
    };

    initializeAuth();
  }, []);

  const login = async (credentials, password) => {
    try {
      // Support both old format (email, password) and new format (credentials object)
      const loginData = typeof credentials === 'string'
        ? { email: credentials, password }
        : credentials;

      const response = await authApi.login(loginData);

      // The login works at more than one shop: the person picks one.
      if (response.success && response.data?.chooseShop) {
        return { success: false, chooseShop: response.data.chooseShop, selectionToken: response.data.selectionToken };
      }

      if (response.success) {
        localStorage.setItem('token', response.data.token);
        localStorage.setItem('user', JSON.stringify(response.data.user));
        setAuthNotice('');
        setUser(response.data.user);
        return { success: true };
      } else {
        return { success: false, message: response.message };
      }
    } catch (error) {
      console.error('Login error:', error);
      // A refusal with a code (a suspended shop, no driver app) says why.
      return {
        success: false,
        code: error.data?.code,
        message: error.data?.code ? error.message : (error.response?.data?.message || 'Login failed')
      };
    }
  };

  const chooseLoginShop = async (selectionToken, tenantId) => {
    try {
      const response = await authApi.chooseLoginShop(selectionToken, tenantId);
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      setAuthNotice('');
      setUser(response.data.user);
      return { success: true };
    } catch (error) {
      console.error('Login shop choice error:', error);
      return { success: false, code: error.data?.code, message: error.message || 'Login failed' };
    }
  };

  const signup = async (userData) => {
    try {
      const response = await authApi.signup(userData);
      return response;
    } catch (error) {
      console.error('Signup error:', error);
      throw error;
    }
  };

  const logout = async () => {
    try {
      await authApi.logout();
    } catch (error) {
      console.error('Logout error:', error);
      // Continue with logout even if API call fails
    }
    
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
  };

  // Exchange the session for a fresh one and reload the account (billing B3:
  // a billing-only session turns into a normal one once the shop is active
  // again, and a yearly cancel turns a normal one billing-only). Returns the
  // account, or null. Throws when the refresh is refused.
  const refreshSession = async () => {
    await authApi.refreshToken();
    const response = await authApi.getProfile();
    if (!response?.success) return null;
    const next = response.data.user;
    localStorage.setItem('user', JSON.stringify(next));
    setUser(next);
    return next;
  };

  const updateProfile = async (userData) => {
    try {
      const response = await authApi.updateProfile(userData);
      if (response.success) {
        const updatedUser = response.data.user;
        setUser(updatedUser);
        localStorage.setItem('user', JSON.stringify(updatedUser));
        return { success: true };
      } else {
        return { success: false, message: response.message };
      }
    } catch (error) {
      console.error('Profile update error:', error);
      return { 
        success: false, 
        message: error.response?.data?.message || 'Failed to update profile' 
      };
    }
  };

  const changePassword = async (currentPassword, newPassword) => {
    try {
      const response = await authApi.changePassword(currentPassword, newPassword);
      return response;
    } catch (error) {
      console.error('Password change error:', error);
      throw error;
    }
  };

  const isAuthenticated = () => {
    return !!user && !!localStorage.getItem('token');
  };

  // A driver whose session started with a one-time password: the server
  // refuses everything but sign-out until the password is changed, and only
  // the Driver app can change it (DriverPasswordChangeGate). Both the flag on
  // the account and the mark on the token are needed: a session the server
  // does not hold (a normal sign-in, or the password already changed) is left alone.
  const passwordChangeRequired = Boolean(
    user && user.accountType === 'driver' && user.mustChangePassword === true && tokenRequiresPasswordChange()
  );

  const isAdmin = () => {
    return hasAdminRights(user);
  };

  const isOwner = () => {
    return userIsOwner(user);
  };

  const isStaff = () => {
    return user && user.role === 'staff';
  };

  // Admin user management functions
  const getAllUsers = async () => {
    if (!isAdmin()) {
      throw new Error('Admin access required');
    }
    return await authApi.admin.getAllUsers();
  };

  const getPendingUsers = async () => {
    if (!isAdmin()) {
      throw new Error('Admin access required');
    }
    return await authApi.admin.getPendingUsers();
  };

  const createUser = async (userData) => {
    if (!isAdmin()) {
      throw new Error('Admin access required');
    }
    return await authApi.admin.createUser(userData);
  };

  const updateUser = async (userId, userData) => {
    if (!isAdmin()) {
      throw new Error('Admin access required');
    }
    return await authApi.admin.updateUser(userId, userData);
  };

  const deleteUser = async (userId) => {
    if (!isAdmin()) {
      throw new Error('Admin access required');
    }
    return await authApi.admin.deleteUser(userId);
  };

  const approveUser = async (userId) => {
    if (!isAdmin()) {
      throw new Error('Admin access required');
    }
    return await authApi.admin.approveUser(userId);
  };

  const rejectUser = async (userId) => {
    if (!isAdmin()) {
      throw new Error('Admin access required');
    }
    return await authApi.admin.rejectUser(userId);
  };

  // Capability checking methods
  const hasCapability = (capability) => {
    return user && user.capabilities && user.capabilities.includes(capability);
  };

  const canTakeOrders = () => {
    return hasCapability('take_orders');
  };

  const canDrive = () => {
    return hasCapability('drive_delivery');
  };

  const isActiveDriver = () => {
    return canDrive() && user && user.driverStatus === 'active';
  };

  const canViewOrders = () => {
    return hasCapability('view_orders');
  };

  const canManageCustomers = () => {
    return hasCapability('manage_customers');
  };

  const getActiveDrivers = async () => {
    if (!isAdmin()) {
      throw new Error('Admin access required');
    }
    return await authApi.admin.getActiveDrivers();
  };

  const updateDriverStatus = async (userId, driverStatus) => {
    if (!isAdmin()) {
      throw new Error('Admin access required');
    }
    return await authApi.admin.updateDriverStatus(userId, driverStatus);
  };

  const value = {
    user,
    loading,
    initialized,
    authNotice,
    login,
    chooseLoginShop,
    signup,
    logout,
    refreshSession,
    passwordChangeRequired,
    updateProfile,
    changePassword,
    isAuthenticated,
    isAdmin,
    isOwner,
    isStaff,
    // Capability checking
    hasCapability,
    canTakeOrders,
    canDrive,
    isActiveDriver,
    canViewOrders,
    canManageCustomers,
    // Admin user management
    getAllUsers,
    getPendingUsers,
    createUser,
    updateUser,
    deleteUser,
    approveUser,
    rejectUser,
    getActiveDrivers,
    updateDriverStatus,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;